import { useState, useCallback } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { imageActionsService, MoveFileRequest } from "./ImageActionsService";
import { logger } from "@/utils/logger";
import { mediaCacheUtils } from "@/core/MediaLibrary/hooks/useMediaQuery";
import { queryClient } from "@/core/queryClient";
import { API_CONFIG } from "@/config/api";
import { UserRole } from "@/services/urlService";

const API_URL = API_CONFIG.URL;

// Helper to get access token
const getAccessToken = (): string | null => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

export interface UseImageActionsReturn {
  isChangingStage: boolean;
  error: string | null;
  updateStage: (
    mediaItem: MediaItem,
    newStage: string,
  ) => Promise<MediaItem | null>;
  moveToStage: (
    mediaItem: MediaItem,
    newStage: string,
    gridPosition?: number,
  ) => Promise<MediaItem | null>;
  clearError: () => void;
}

export const useImageActions = (
  clientId: string | null,
): UseImageActionsReturn => {
  const [isChangingStage, setIsChangingStage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateStage = useCallback(
    async (
      mediaItem: MediaItem,
      newStage: string,
    ): Promise<MediaItem | null> => {
      if (!clientId) {
        setError("Client ID is required");
        return null;
      }

      if (mediaItem.stage === newStage) {
        logger.debug(
          `Media item ${mediaItem.getName()} is already in stage ${newStage}`,
        );
        return mediaItem;
      }

      setIsChangingStage(true);
      setError(null);

      try {
        logger.info(
          `🔄 Updating stage for ${mediaItem.getName()} from ${mediaItem.stage} to ${newStage}`,
        );

        const accessToken = getAccessToken();
        if (!accessToken) {
          throw new Error("Access token required to update media item");
        }

        // Make API call to update the media item
        const response = await fetch(
          `${API_URL}/admin/media/${mediaItem.id}`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              clientId: clientId,
              stage: newStage,
              stage_hashtag: `#${newStage}`,
              label: mediaItem.label, // Preserve the label
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update media item');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Update operation failed');
        }

        // Create updated MediaItem from response
        const updatedMediaItem = MediaItem.fromBackend(
          result.mediaItem,
          UserRole.ADMIN,
          clientId
        );

        // Update React Query cache - upsert handles stage-filtered caches
        // It removes items from caches that don't match the new stage
        // and adds/updates items in caches that do match
        mediaCacheUtils.upsertMediaItem(clientId, updatedMediaItem);

        // NOTE: We don't call invalidateAllMedia here because:
        // 1. upsertMediaItem already handles moving items between stage-filtered caches
        // 2. Invalidating would cause a refetch that might race with navigation

        // Approving story media auto-creates a DRAFT story post server-side
        // (and un-approving cascade-cancels it), but no post SSE event fires
        // for that hook — so the Stories board query must be invalidated here
        // or the new story only appears after a manual refresh.
        if (mediaItem.getContentType() === "stories") {
          queryClient.invalidateQueries({ queryKey: ["stories", clientId] });
        }

        logger.info(`✅ Successfully updated stage to ${newStage}`);
        return updatedMediaItem;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        logger.error("❌ Error updating stage:", err);
        setError(errorMessage);
        return null;
      } finally {
        setIsChangingStage(false);
      }
    },
    [clientId],
  );

  const moveToStage = useCallback(
    async (
      mediaItem: MediaItem,
      newStage: string,
      gridPosition?: number,
    ): Promise<MediaItem | null> => {
      if (!clientId) {
        setError("Client ID is required");
        return null;
      }

      if (mediaItem.stage === newStage) {
        logger.debug(
          `Image ${mediaItem.getName()} is already in stage ${newStage}`,
        );
        return mediaItem;
      }

      setIsChangingStage(true);
      setError(null);

      try {
        const moveRequest: MoveFileRequest = {
          fileId: mediaItem.fileIdentity,
          fileName: String(mediaItem.getName()),
          fromStage: String(mediaItem.stage || "ready"),
          toStage: String(newStage),
          contentType: String(mediaItem.getContentType()),
        };

        logger.info(
          `🔄 Moving image ${mediaItem.getName()} from ${mediaItem.stage} to ${newStage}`,
        );
        logger.debug("🔍 Move request data:", moveRequest);

        const response = await imageActionsService.moveFileBetweenStages(
          clientId,
          moveRequest,
        );

        if (response.success) {
          const updatedMediaItem = mediaItem.withStage(newStage);

          logger.info(`✅ Successfully moved image to ${newStage}`, response);
          return updatedMediaItem;
        } else {
          throw new Error(
            response.message || response.error || "Failed to move image",
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        logger.error("❌ Error moving image:", err);
        setError(errorMessage);
        return null;
      } finally {
        setIsChangingStage(false);
      }
    },
    [clientId],
  );

  return {
    isChangingStage,
    error,
    updateStage,
    moveToStage,
    clearError,
  };
};
